import { SignatureV4 } from "@aws-sdk/signature-v4";
import { Sha256 } from "@aws-crypto/sha256-browser";
import { HttpRequest } from "@aws-sdk/protocol-http";
import { API, Auth } from "aws-amplify";

/**
 * POST a JSON body to an AWS Lambda Function URL using SigV4 (IAM) signing.
 * Both the bulk image-generation pipeline and single-image regeneration use
 * IAM-authed Function URLs, so the request must be signed with the caller's
 * Cognito credentials. Returns the parsed JSON response (unwrapping the common
 * `{ body: "<json string>" }` Lambda-proxy envelope when present).
 */
export const signedLambdaFetch = async (
  url: string,
  body: Record<string, any>
): Promise<{ ok: boolean; status: number; data: any }> => {
  const lambdaUrl = new URL(url);
  const credentials = await Auth.currentCredentials();
  const requestBody = JSON.stringify(body);

  const request = new HttpRequest({
    method: "POST",
    hostname: lambdaUrl.hostname,
    path: lambdaUrl.pathname,
    headers: {
      "Content-Type": "application/json",
      host: lambdaUrl.hostname,
    },
    body: requestBody,
  });

  const signer = new SignatureV4({
    credentials,
    region: process.env.NEXT_PUBLIC_AWS_REGION || "eu-central-1",
    service: "lambda",
    sha256: Sha256,
  });

  const signedRequest = await signer.sign(request);
  // Drop the `host` header — fetch sets it and browsers reject it as unsafe.
  const { host, ...headersForRequest } = signedRequest.headers;

  const res = await fetch(url, {
    method: "POST",
    headers: headersForRequest,
    body: requestBody,
  });

  const json = await res.json();
  const data =
    typeof json.body === "string"
      ? JSON.parse(json.body)
      : json.body || json;

  return { ok: res.ok, status: res.status, data };
};

export interface ProjectImageSettings {
  guidelines: string;
  coverAspectRatio: string;
}

/**
 * Fetch the image-generation settings (brand style guidelines + default cover
 * aspect ratio) of the project that owns a brief/article. Falls back to empty
 * guidelines / 16:9 when unavailable (generation proceeds without brand
 * context rather than failing).
 */
export const fetchProjectImageSettings = async (
  articleId: string
): Promise<ProjectImageSettings> => {
  try {
    const briefData = await API.get("brief", `/briefs/object/${articleId}`, {});
    if (briefData?.projectId) {
      const settingsRes = await API.get(
        "projects",
        `/projects/getBrandStyleGuidelines/${briefData.projectId}`,
        {}
      );
      if (settingsRes?.success) {
        return {
          guidelines: settingsRes.guidelines || "",
          coverAspectRatio: settingsRes.coverImageAspectRatio || "16:9",
        };
      }
    }
  } catch (err) {
    console.warn(
      "Could not fetch project image settings, proceeding with defaults:",
      err
    );
  }
  return { guidelines: "", coverAspectRatio: "16:9" };
};

/**
 * Fetch only the brand style guidelines for the project that owns a
 * brief/article. Returns an empty string when unavailable.
 */
export const fetchBrandGuidelines = async (
  articleId: string
): Promise<string> => {
  const { guidelines } = await fetchProjectImageSettings(articleId);
  return guidelines;
};

export interface RegenerateImageParams {
  articleId: string;
  opportunity: Record<string, any>;
  userFeedback: string;
  brandStyleGuidelines?: string;
  /** URL of the current image — sent to Gemini as content so the
   * regeneration adjusts it instead of generating from scratch. */
  previousImageUrl?: string;
}

export interface RegenerateImageResult {
  imageUrl: string;
  metadata: Record<string, any>;
}

/**
 * Regenerate a single image from its original generation "opportunity" plus a
 * free-text user comment ("what I didn't like / what I want"). Calls the
 * regenerate Lambda Function URL and returns the new image URL + refreshed
 * opportunity metadata to persist back on the editor node.
 */
export const regenerateImage = async ({
  articleId,
  opportunity,
  userFeedback,
  brandStyleGuidelines,
  previousImageUrl,
}: RegenerateImageParams): Promise<RegenerateImageResult> => {
  const url = process.env.NEXT_PUBLIC_IMAGE_REGENERATION_URL;
  if (!url) {
    throw new Error("Image regeneration is not configured");
  }

  const { ok, data } = await signedLambdaFetch(url, {
    article_id: articleId,
    article_url: `article-${articleId}`,
    opportunity,
    user_feedback: userFeedback,
    brand_style_guidelines: brandStyleGuidelines || "",
    previous_image_url: previousImageUrl || "",
  });

  if (!ok || !data?.success || !data?.image_url) {
    throw new Error(data?.error || "Image regeneration failed");
  }

  return {
    imageUrl: data.image_url,
    metadata: data.metadata || opportunity,
  };
};
